import type { JDDetailDTO } from "@/modules/jd/domain/contracts";
import { formatDateLocal, formatDateTimeLocal } from "@/shared/lib/date-time";

interface JDPdfPreviewContentProps {
  jd: JDDetailDTO;
}

const frequencyLabels: Record<string, string> = {
  daily: "ປະຈຳວັນ",
  weekly: "ປະຈຳອາທິດ",
  monthly: "ປະຈຳເດືອນ",
  quarterly: "ປະຈຳໄຕມາດ",
  annual: "ປະຈຳປີ",
};

const requirementTypeLabels: Record<string, string> = {
  experience: "ປະສົບການ",
  skills: "ທັກສະ",
  knowledge: "ຄວາມຮູ້",
  education: "ການສຶກສາ",
  other: "ອື່ນໆ",
};

export function JDPdfPreviewContent({ jd }: JDPdfPreviewContentProps) {
  return (
    <div
      id="jd-pdf-content"
      className="mx-auto max-w-4xl bg-white p-8 font-serif text-gray-900 leading-relaxed print:max-w-none print:p-0 print:font-serif print:text-gray-900 print:leading-relaxed"
      style={{
        fontFamily: '"Times New Roman", Times, serif',
        lineHeight: "1.5",
        color: "#1a1a1a",
      }}
    >
      {/* PDF Header - Letterhead Style */}
      <div className="mb-8 border-gray-800 border-b-4 pb-6 print:mb-6">
        <div className="mb-6 text-center">
          <h1 className="font-bold text-4xl text-gray-800 uppercase tracking-wider">
            FREEDOM Sole Co.Ltd
          </h1>
          <p className="mt-2 text-gray-600 text-lg">Job Description</p>
          <div className="mx-auto mt-4 h-1 w-24 bg-gray-800" />
        </div>

        <div className="flex items-start justify-between">
          <div className="text-sm">
            <p className="font-semibold">
              ເອກະສານເລກທີ:{" "}
              <span className="font-normal">{`JD-${jd.id.slice(-6).toUpperCase()}`}</span>
            </p>
            <p className="mt-1 font-semibold">
              ວັນທີອັບເດດລ່າສຸດ:{" "}
              <span className="font-normal">{formatDateLocal(jd.updatedAt)}</span>
            </p>
          </div>
          <div className="text-right text-sm">
            <p className="font-semibold">ວັນທີພິມ:</p>
            <p className="font-normal">{formatDateLocal(new Date())}</p>
          </div>
        </div>
      </div>

      {/* JD Title and Status */}
      <div className="mb-12 text-center">
        <h2 className="mb-6 border-gray-300 border-b-2 pb-4 font-bold text-3xl text-gray-800 uppercase tracking-wide">
          {jd.title}
        </h2>
      </div>

      {/* Basic Information Table */}
      <div className="mb-10">
        <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
          1. ຂໍ້ມູນພື້ນຖານ
        </h3>

        <table className="mb-6 w-full border-collapse border-2 border-gray-400">
          <tbody>
            <tr className="border-gray-300 border-b">
              <td className="w-1/3 border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ພະແນກ
              </td>
              <td className="p-4 text-sm">{jd.departmentName || "ບໍ່ໄດ້ກໍານົດ"}</td>
            </tr>
            <tr className="border-gray-300 border-b">
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ລະດັບ
              </td>
              <td className="p-4 text-sm">{jd.level || "ບໍ່ໄດ້ກໍານົດ"}</td>
            </tr>
            {jd.reportsToPositionTitle && (
              <tr className="border-gray-300 border-b">
                <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                  ລາຍງານໄປຫາ
                </td>
                <td className="p-4 text-sm">{jd.reportsToPositionTitle}</td>
              </tr>
            )}
            {jd.managesCount && (
              <tr className="border-gray-300 border-b">
                <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                  ດູແລຈຳນວນຄົນ
                </td>
                <td className="p-4 text-sm">{jd.managesCount} ຄົນ</td>
              </tr>
            )}
            <tr>
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ວັນທີອັບເດດລ່າສຸດ
              </td>
              <td className="p-4 text-sm">{formatDateTimeLocal(jd.updatedAt)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Main Responsibilities */}
      {jd.responsibilities && jd.responsibilities.length > 0 && (
        <div className="mb-10">
          <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
            2. ຫນ້າທີ່ຫຼັກ
          </h3>

          <div className="space-y-3">
            {jd.responsibilities.map((resp, index) => (
              <div key={resp.id} className="flex items-start gap-3">
                <span className="mt-1 font-bold text-gray-800">
                  {index + 1}.
                </span>
                <div
                  className="text-base text-gray-800 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: resp.description }}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Routine Tasks */}
      {jd.routineTasks && jd.routineTasks.length > 0 && (
        <div className="mb-10">
          <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
            3. ວຽກປະຈຳ
          </h3>

          <div className="space-y-6">
            {["daily", "weekly", "monthly", "quarterly", "annual"].map(
              (freq) => {
                const tasks = jd.routineTasks.filter(
                  (t) => t.frequency === freq,
                );
                if (tasks.length === 0) return null;
                return (
                  <div key={freq} className="mb-4">
                    <h4 className="mb-3 font-semibold text-base text-gray-700 uppercase tracking-wide">
                      {frequencyLabels[freq]}
                    </h4>
                    <div className="ml-4 space-y-2">
                      {tasks.map((task, index) => (
                        <div key={task.id} className="flex items-start gap-3">
                          <span className="mt-1 text-gray-600">•</span>
                          <p className="text-base text-gray-800 leading-relaxed">
                            {task.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              },
            )}
          </div>
        </div>
      )}

      {/* Requirements */}
      {jd.requirements && jd.requirements.length > 0 && (
        <div className="mb-10">
          <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
            4. ຄຸນສົມບັດທີ່ຕ້ອງການ
          </h3>

          <div className="space-y-6">
            {["experience", "skills", "knowledge", "education", "other"].map(
              (type) => {
                const reqs = jd.requirements.filter((r) => r.type === type);
                if (reqs.length === 0) return null;
                return (
                  <div key={type} className="mb-4">
                    <h4 className="mb-3 font-semibold text-base text-gray-700 uppercase tracking-wide">
                      {requirementTypeLabels[type]}
                    </h4>
                    <div className="ml-4 space-y-2">
                      {reqs.map((req, index) => (
                        <div key={req.id} className="flex items-start gap-3">
                          <span className="mt-1 text-gray-600">•</span>
                          <div className="flex-1">
                            <p className="text-base text-gray-800 leading-relaxed">
                              {req.description}
                            </p>
                            {req.isRequired && (
                              <span className="mt-1 inline-block text-gray-600 text-xs">
                                (ຈຳເປັນ)
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              },
            )}
          </div>
        </div>
      )}

      {/* Footer - Signatures and Approval */}
      <div className="mt-16 border-gray-800 border-t-4 pt-8">
        <div className="grid grid-cols-2 gap-8 text-sm">
          <div>
            <p className="mb-2 font-bold text-gray-800 uppercase tracking-wide">
              ເອກະສານອະນຸມັດໂດຍ
            </p>
            <div className="mb-2 border-gray-400 border-b pb-8" />
            <p className="text-gray-600 text-xs">ຊື່-ນາມສະກຸນ, ຕໍາແໜ່ງ</p>
            <p className="text-gray-600 text-xs">ວັນທີ: _______________</p>
          </div>
          <div>
            <p className="mb-2 font-bold text-gray-800 uppercase tracking-wide">
              ເອກະສານທົບທວນໂດຍ
            </p>
            <div className="mb-2 border-gray-400 border-b pb-8" />
            <p className="text-gray-600 text-xs">ຊື່-ນາມສະກຸນ, ຕໍາແໜ່ງ</p>
            <p className="text-gray-600 text-xs">ວັນທີ: _______________</p>
          </div>
        </div>

        <div className="mt-8 border-gray-300 border-t pt-6 text-center text-gray-500 text-xs">
          <p className="font-medium">
            ເອກະສານນີ້ໄດ້ຮັບການສ້າງຈາກລະບົບຈັດການ JD - FREEDOM Sole Co.Ltd
          </p>
          <p className="mt-2">
            ເລກເອກະສານ: {`JD-${jd.id.slice(-6).toUpperCase()}`} | ພິມເມື່ອ:{" "}
            {formatDateLocal(new Date())}
          </p>
        </div>
      </div>
    </div>
  );
}
